import { useCallback, useEffect, useRef, useState } from 'react';
import { listReminderTemplates } from '../../../../api/collections';
import { apiErrorMessage, apiErrorStatus } from '../utils/collectionReminders';

/**
 * Plantillas de recordatorios del operador (GET billing/collections/templates): las activas,
 * la del sistema por canal, el catálogo de variables y los límites. Billing es la única
 * fuente de las variables y los topes: el front no los copia.
 *
 * Si una recarga falla se conserva el último catálogo bueno (con `error` informado), igual
 * que `useCollectionsSetup`: vaciar la lista haría parecer que no hay plantillas.
 *
 * @param {{ enabled?: boolean }} options
 * @returns {{ loading: boolean, error: { status: number|null, message: string|null } | null,
 *   catalog: { items: object[], system: object, variables: object[], limits: object } | null,
 *   reload: () => Promise<object|null> }} `reload` resuelve con el catálogo nuevo, o `null`
 *   si falló o quedó viejo por otra recarga posterior.
 */
export default function useReminderTemplates({ enabled = true } = {}) {
  const [state, setState] = useState({ loading: enabled, error: null, catalog: null });
  // Solo la última carga escribe: un "Reintentar" rápido no debe pisar con una respuesta vieja.
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    let data = null;
    let failure = null;
    try {
      ({ data } = await listReminderTemplates());
    } catch (err) {
      failure = err;
    }
    if (seq !== requestSeq.current) return null;
    setState((s) => ({
      loading: false,
      error: failure ? { status: apiErrorStatus(failure), message: apiErrorMessage(failure) } : null,
      catalog: data ?? s.catalog,
    }));
    return data ?? null;
  }, []);

  useEffect(() => {
    if (enabled) load();
    return () => {
      requestSeq.current += 1;
    };
  }, [enabled, load]);

  return { ...state, reload: load };
}
